import { faker } from '@faker-js/faker';

import {
  ReaderTaskData,
  BodyRegions,
  GradeTypeLabel,
  ReaderTaskDraftDetails,
  ReaderScoringScoresScores,
} from '@dynamika-shared/types/reader';

type SegmentsName =
  | 'Lumbar Spine L1'
  | 'Lumbar Spine L2'
  | 'Lumbar Spine L3'
  | 'Lumbar Spine L4';

type Segments = 'L1' | 'L2' | 'L3' | 'L4';

type SegmentsMap = {
  [key in SegmentsName]: Segments;
};

const segmentsMap: SegmentsMap = {
  'Lumbar Spine L1': 'L1',
  'Lumbar Spine L2': 'L2',
  'Lumbar Spine L3': 'L3',
  'Lumbar Spine L4': 'L4',
};

const gradeLabels: GradeTypeLabel[] = [
  'Grade 0',
  'Grade 1',
  'Grade 2',
  'Grade 3',
  'NE',
];

const bodyRegionsWithoutSegments: BodyRegions[] = [
  'Lumbar Spine',
  'Hip',
  'Femoral Neck',
  'Distal 1/3 Radius',
];

export const createRandomInners = (
  bodyRegionsName: BodyRegions
): ReaderScoringScoresScores => {
  const lumbars: BodyRegions[] =
    bodyRegionsName === 'Lumbar Spine'
      ? [
          'Lumbar Spine L1',
          'Lumbar Spine L2',
          'Lumbar Spine L3',
          'Lumbar Spine L4',
        ]
      : ['Hip', 'Femoral Neck', 'Distal 1/3 Radius'];

  const bodyRegion = faker.helpers.arrayElement([...lumbars]);
  const bodyProperties =
    bodyRegionsName === 'Lumbar Spine'
      ? segmentsMap[bodyRegion as keyof typeof segmentsMap]
      : faker.string.alphanumeric({
          length: { min: 3, max: 9 },
        });
  return {
    labelId: faker.string.uuid(),
    bodyRegion: bodyRegion,
    bodyProperty: bodyProperties,
    densitometryScore: {
      bmd: faker.number.float({ max: 100 }),
      bmc: faker.number.float({ max: 100 }),
      area: faker.number.float({ max: 100 }),
    },
    tScore: faker.number.float({ max: 100 }),
    selected: faker.datatype.boolean(),
    grade: faker.helpers.arrayElement([...gradeLabels]),
  };
};

export const createRandomInnerScores = (
  count: number,
  bodyRegion: BodyRegions
): ReaderScoringScoresScores[] => {
  return faker.helpers.multiple(() => createRandomInners(bodyRegion), {
    count,
  });
};

export const createRandomScores = (
  segmentName: BodyRegions
): ReaderTaskDraftDetails => {
  const inners = segmentName === 'Lumbar Spine' ? 4 : 1;
  return {
    bodyRegion: segmentName,
    bodyProperty: faker.string.alphanumeric({
      length: { min: 3, max: 9 },
    }),
    bmd: faker.number.float({ max: 100 }),
    tScore: faker.number.float({ max: 100 }),
    scores: createRandomInnerScores(inners, segmentName),
    selected: faker.datatype.boolean(),
  };
};

export const scores = (): ReaderTaskDraftDetails[] => {
  const scoresResult = bodyRegionsWithoutSegments.map((segment: BodyRegions) =>
    createRandomScores(segment)
  );

  return scoresResult;
};

export const createRandomDXAScoring = (): ReaderTaskData => {
  return {
    comment: faker.string.alphanumeric({
      length: { min: 0, max: 12 },
    }),
    scores: scores(),
    referencePopulation: {
      dxaDeviceType: faker.string.alphanumeric({
        length: { min: 3, max: 8 },
      }),
      id: faker.string.uuid(),
      manufacturer: faker.string.alphanumeric({
        length: { min: 3, max: 8 },
      }),
      name: faker.string.alphanumeric({
        length: { min: 3, max: 8 },
      }),
      softwareVersion: faker.string.alphanumeric({
        length: { min: 3, max: 8 },
      }),
    },
  };
};
